import { useEffect, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle } from '../../../icons';
import { tShared, getSharedComponentLang, subscribeSharedComponentLang } from '../../../lib/shared-i18n';

/**
 * PreviewUnavailableModal (chat-file-preview — 2026-07-02) — 미리보기를 지원하지 않는 형식의 파일
 * (zip/hwp 등)을 클릭했을 때 뜨는 경고 모달. 미리보기 가능 형식은 이 모달 대신 우측 미리보기
 * 패널이 바로 열린다(호출부에서 분기). 오버레이/닫기 UX 는 기존 chat-modal-* 규약 재사용.
 */
export function PreviewUnavailableModal({ name, onClose }: { name?: string; onClose: () => void }) {
  useSyncExternalStore(subscribeSharedComponentLang, getSharedComponentLang, getSharedComponentLang);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // chat-modal-portal (2026-07-02) — body 로 portal: 채팅 트리의 z-index:1 트랩을 벗어나
  // 모달이 입력창·미리보기 전체를 덮고 항상 최상단(--z-modal).
  return createPortal(
    <div className="chat-modal-overlay" onMouseDown={onClose} role="presentation">
      <div
        className="chat-modal chat-preview-warn"
        role="alertdialog"
        aria-modal="true"
        aria-label={tShared('shared.attach.preview_unavailable_title')}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="chat-modal-header">
          <h2 className="chat-modal-title">{tShared('shared.attach.preview_unavailable_title')}</h2>
          <button
            type="button"
            className="chat-modal-close"
            onClick={onClose}
            aria-label={tShared('shared.attach.close')}
          >
            <X size={18} />
          </button>
        </header>

        <div className="chat-preview-warn-body">
          <span className="chat-preview-warn-icon" aria-hidden>
            <AlertTriangle size={22} />
          </span>
          <p className="chat-preview-warn-msg">
            {name ? `${name} — ` : ''}
            {tShared('shared.attach.preview_unavailable')}
          </p>
        </div>

        <div className="chat-preview-warn-actions">
          <button type="button" className="chat-preview-warn-ok" onClick={onClose}>
            {tShared('shared.attach.confirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
